import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Star, Play, Loader2, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { CourseMediaItem } from '../../types';

interface GalleryItemCardProps {
  item: CourseMediaItem;
  isCover: boolean;
  progress?: number; // processing % for an uploading/processing video
  onRemove: () => void;
  onMakeCover: () => void;
  onChooseCover?: () => void; // pick the poster frame for an uploaded video
}

export default function GalleryItemCard({ item, isCover, progress, onRemove, onMakeCover, onChooseCover }: GalleryItemCardProps) {
  const { t } = useTranslation('teaching');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const thumb = item.type === 'image' ? item.url || item.posterUrl : item.posterUrl;

  return (
    <div ref={setNodeRef} style={style} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-100 aspect-video">
      {thumb ? (
        <img src={thumb} alt={item.caption || ''} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          {item.type === 'video' ? <Play className="w-8 h-8" /> : <ImageIcon className="w-8 h-8" />}
        </div>
      )}

      {/* video overlays */}
      {item.type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {item.videoStatus === 'processing' || item.videoStatus === 'uploading' ? (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-black/60 text-white text-xs"><Loader2 className="w-3 h-3 animate-spin" />{t('courseEdit.videoProcessing')}{typeof progress === 'number' && progress > 0 ? ` ${Math.round(progress)}%` : ''}</span>
          ) : item.videoStatus === 'failed' ? (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-red-600/80 text-white text-xs"><AlertTriangle className="w-3 h-3" />{t('courseEdit.videoFailed')}</span>
          ) : (
            <Play className="w-8 h-8 text-white/90 fill-white/90 drop-shadow" />
          )}
        </div>
      )}

      {/* processing progress bar */}
      {item.type === 'video' && (item.videoStatus === 'processing' || item.videoStatus === 'uploading') && typeof progress === 'number' && progress > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div className="h-full bg-gold transition-all duration-500" style={{ width: `${Math.round(progress)}%` }} />
        </div>
      )}

      {/* cover badge */}
      {isCover && (
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-gold text-white text-[10px] font-medium">
          {t('courseEdit.coverBadge')}
        </span>
      )}

      {/* drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-1.5 right-1.5 p-1 rounded bg-black/40 text-white cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
        title={t('courseEdit.dragToReorder')}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* actions */}
      <div className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onChooseCover && item.type === 'video' && item.videoSource === 'upload' && item.videoStatus === 'ready' && (
          <button type="button" onClick={onChooseCover} className="p-1.5 rounded bg-black/50 text-white hover:bg-gold transition-colors" title={t('courseEdit.chooseCover')}>
            <ImageIcon className="w-3.5 h-3.5" />
          </button>
        )}
        {!isCover && (
          <button type="button" onClick={onMakeCover} className="p-1.5 rounded bg-black/50 text-white hover:bg-gold transition-colors" title={t('courseEdit.makeCover')}>
            <Star className="w-3.5 h-3.5" />
          </button>
        )}
        <button type="button" onClick={onRemove} className="p-1.5 rounded bg-black/50 text-white hover:bg-red-600 transition-colors" title={t('courseEdit.removeItem')}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
